// src/app/routes.tsx
import React, { Suspense } from 'react';
import { Navigate, createBrowserRouter, Outlet, useLocation, type RouteObject } from 'react-router-dom';

import Layout        from '@/shared/components/Layout/Layout';
import Login         from '@/features/system/auth/Login';
import RequireAuth   from '@/features/system/auth/RequireAuth';
import Device        from '@/features/system/auth/Device';
import { ChunkErrorBoundary } from '@/shared/components/ChunkErrorBoundary';
import { lazyWithRetry }      from '@/utils/lazyWithRetry';

import AuthDisconnectGate from '@/shared/auth/AuthDisconnectGate';
import StockDashboard         from '@/features/services/stock/components/StockDashboard';
import DeveloperDashboard     from '@/features/system/developer/components/DeveloperDashboard';
import SalesPurchaseDashboard from '@/features/services/sales/components/SalesPurchaseDashboard';
import AgentDashboard         from '@/features/services/agent/components/AgentDashboard';
import AdminDashboard         from '@/features/system/admin/components/AdminDashboard';
import Dashboard_media        from '@/features/services/media/components/Dashboard_media';
import Dashboard_media_youtube from '@/features/services/media/components/Dashboard_media_youtube';
import MediaDashboard          from '@/features/services/media-v2/components/MediaDashboard';
import RoleSwitch from '@/shared/auth/RoleSwitch';
import { useAuth } from '@/shared/auth';
import { isVisitorUser } from '@/shared/auth/isVisitorUser';
import {
  canAccessDeveloperSurface,
  resolveAuthenticatedHome,
  shouldForceTableOpsLanding,
} from '@/shared/auth/shouldForceTableOpsLanding';
import { ttlockRoutes } from '@/features/services/ttlock/routes';
import UsageDashboard from '@/features/usage/components/UsageDashboard';
import QuotaAdmin     from '@/features/usage/components/QuotaAdmin';

// Public landing — anonymous visitor home at `/`
import RootGate from './RootGate';

// Operator kiosk — full-bleed, RBAC-gated table-ops SPA (no sidebar/Layout)
const TableOpsOperatorApp = lazyWithRetry(() =>
  import('@/features/tableops/operator/TableOpsOperatorApp')
);

const VideoSpaApp = lazyWithRetry(() =>
  import('@/features/video/operator/VideoSpaApp')
);

const TelemetryCenterPage = lazyWithRetry(() =>
  import('@/features/telemetry-center/components/TelemetryCenterPage')
);

const AgentChatPage = lazyWithRetry(() =>
  import('@/features/services/agent-chat/components/AgentChatPage')
);

function DeveloperRouteGate() {
  const location = useLocation();
  const { user, capabilities, nav, loading } = useAuth();
  if (loading) return <div className="p-4">Loading…</div>;
  if (!user || isVisitorUser(user)) {
    return <Navigate to={{ pathname: '/', search: location.search }} replace />;
  }
  const policy = { user, capabilities, nav };
  if (shouldForceTableOpsLanding(policy)) {
    return <Navigate to="/tableops" replace />;
  }
  if (!canAccessDeveloperSurface(policy)) {
    return <Navigate to={resolveAuthenticatedHome(policy)} replace />;
  }
  return <DeveloperDashboard />;
}

// Shared scope — zero-trust, no auth required (lazy-loaded)
const SharedScopePage = lazyWithRetry(() =>
  import('@/features/shared-scope/pages/SharedScopePage')
);

// Shared telemetry view — parameterised IoT view link (lazy-loaded)
const SharedTelemetryViewPage = lazyWithRetry(() =>
  import('@/features/system/devices/components/iot/SharedTelemetryViewPage')
);

// Lazy chunks
const TableOrder = lazyWithRetry(() =>
  import('@/features/services/order/components/TableOrder')
);
const OrderDashboard = lazyWithRetry(() =>
  import('@/features/services/order/components/OrderDashboard')
);
const LegalDashboard = lazyWithRetry(() =>
  import('@/features/services/legal-ops/components/LegalDashboard')
);

// All routes that require auth & the app layout
const protectedChildren: RouteObject[] = [
  {
    path: 'cash-register',
    element: (
      <Suspense fallback={<div className="p-4">Loading…</div>}>
        <DeveloperDashboard />
      </Suspense>
    ),
  },

  {
    path: 'order',
    element: (
      <Suspense fallback={<div className="p-4">Loading order…</div>}>
        <RoleSwitch
          capability="canAdminPurchaseTable"
          whenAllowed={TableOrder}
          whenDenied={OrderDashboard}
        />
      </Suspense>
    ),
  },

  { path: 'stock',          element: <StockDashboard /> },
  { path: 'sales-purchase', element: <SalesPurchaseDashboard /> },
  { path: 'agent',          element: <AgentDashboard /> },
  {
    path: 'agent-chat',
    element: (
      <Suspense fallback={<div className="p-4">Loading chat…</div>}>
        <AgentChatPage />
      </Suspense>
    ),
  },
  { path: 'media',              element: <MediaDashboard /> },
  { path: 'media/legacy',        element: <Dashboard_media /> },
  { path: 'media/youtube',      element: <Dashboard_media_youtube /> },
  { path: 'developer',      element: <DeveloperRouteGate /> },
  { path: 'admin',          element: <AdminDashboard /> },

  // Metering — usage dashboard & quota admin
  { path: 'usage',       element: <UsageDashboard /> },
  { path: 'quota-admin', element: <QuotaAdmin /> },

  // Telemetry Center — cross-entity hot-stream dashboard
  {
    path: 'telemetry-center',
    element: (
      <Suspense fallback={<div className="p-4">Loading Telemetry Center…</div>}>
        <TelemetryCenterPage />
      </Suspense>
    ),
  },

  // Legal practice management
  {
    path: 'legal',
    element: (
      <Suspense fallback={<div className="p-4">Loading Legal…</div>}>
        <LegalDashboard />
      </Suspense>
    ),
  },

  // Plug in TTLock feature routes (absolute or relative are both fine)
  ...ttlockRoutes,

  // NotFound
  { path: '*', element: <div className="p-6">Not found</div> },
];

export const router = createBrowserRouter([
  // Root wrapper so global UI (auth disconnect popup) works on all routes
  {
    element: (
      <ChunkErrorBoundary>
        <AuthDisconnectGate />
        <Outlet />
      </ChunkErrorBoundary>
    ),
    children: [
      // Public
      { path: '/', element: <RootGate /> },
      { path: '/login', element: <Login /> },
      { path: '/device', element: <Device /> },

      // Shared scope — zero-trust visitor access (no auth, no session)
      {
        path: '/s/:token',
        element: (
          <Suspense fallback={
            <div className="min-h-screen grid place-items-center bg-black text-neutral-400">
              <div className="animate-pulse">Loading shared view…</div>
            </div>
          }>
            <SharedScopePage />
          </Suspense>
        ),
      },

      // Shared telemetry view link — parameterised IoT telemetry (no auth)
      {
        path: '/s/v/:token',
        element: (
          <Suspense fallback={
            <div className="min-h-screen grid place-items-center bg-black text-neutral-400">
              <div className="animate-pulse">Loading shared view…</div>
            </div>
          }>
            <SharedTelemetryViewPage />
          </Suspense>
        ),
      },

      // Guarded area
      {
        element: <RequireAuth />, // <- uses <Outlet/> internally
        children: [
          // Operator kiosk — full-bleed, OUTSIDE <Layout/> (no sidebar).
          // The shell itself enforces the RBAC redirect for non-operators.
          {
            path: '/tableops',
            element: (
              <Suspense fallback={
                <div className="min-h-screen grid place-items-center bg-black text-neutral-400">
                  <div className="animate-pulse">Loading…</div>
                </div>
              }>
                <TableOpsOperatorApp />
              </Suspense>
            ),
          },
          {
            path: '/video',
            element: (
              <Suspense fallback={
                <div className="min-h-screen grid place-items-center bg-black text-neutral-400">
                  <div className="animate-pulse">Loading video gallery...</div>
                </div>
              }>
                <VideoSpaApp />
              </Suspense>
            ),
          },
          {
            element: <Layout />, // app shell
            children: protectedChildren,
          },
        ],
      },
    ],
  }
]);
